import { normalizeAgentId } from "../../../../src/routing/session-key.js";
import {
  findAgentListIndex,
  getAgentsConfigList,
  type AgentListEntryRecord,
} from "./agents-config.ts";

export const AGENT_TOOL_PROFILE_OPTIONS = [
  { id: "minimal", label: "Minimal" },
  { id: "coding", label: "Coding" },
  { id: "messaging", label: "Messaging" },
  { id: "full", label: "Full" },
] as const;

export type ConfiguredModelOption = {
  value: string;
  label: string;
};

function ensureAgentEntry(
  config: Record<string, unknown>,
  agentId: string,
): { list: AgentListEntryRecord[]; index: number } {
  const list = getAgentsConfigList(config);
  const agents = (config.agents ?? {}) as Record<string, unknown>;
  if (!config.agents || typeof config.agents !== "object") {
    config.agents = agents;
  }
  if (!Array.isArray((agents as { list?: unknown }).list)) {
    (agents as { list: AgentListEntryRecord[] }).list = list;
  }
  const normalized = normalizeAgentId(agentId);
  let index = findAgentListIndex(config, normalized);
  if (index < 0) {
    list.push({ id: normalized });
    index = list.length - 1;
    (agents as { list: AgentListEntryRecord[] }).list = list;
  }
  return { list, index };
}

export function resolveConfiguredModels(
  config: Record<string, unknown> | null,
): ConfiguredModelOption[] {
  const models = (config?.agents as { defaults?: { models?: Record<string, unknown> } } | undefined)
    ?.defaults?.models;
  if (!models || typeof models !== "object") {
    return [];
  }
  const options: ConfiguredModelOption[] = [];
  for (const [modelId, modelRaw] of Object.entries(models)) {
    const trimmed = modelId.trim();
    if (!trimmed) {
      continue;
    }
    const alias =
      modelRaw && typeof modelRaw === "object" && "alias" in modelRaw
        ? typeof (modelRaw as { alias?: unknown }).alias === "string"
          ? (modelRaw as { alias?: string }).alias?.trim()
          : undefined
        : undefined;
    const label = alias && alias !== trimmed ? `${alias} (${trimmed})` : trimmed;
    options.push({ value: trimmed, label });
  }
  return options;
}

export function resolveEffectiveModelLabel(
  config: Record<string, unknown> | null,
  agentId: string,
): string {
  if (!config) {
    return "—";
  }
  const entry = getAgentsConfigList(config).find(
    (e) => normalizeAgentId(e.id ?? "") === normalizeAgentId(agentId),
  );
  const entryModel = entry?.model;
  if (typeof entryModel === "string" && entryModel.trim()) {
    return entryModel.trim();
  }
  if (entryModel && typeof entryModel === "object") {
    const primary = (entryModel as { primary?: string }).primary;
    if (primary?.trim()) {
      return primary.trim();
    }
  }
  const defaults = (config.agents as { defaults?: { model?: unknown } })?.defaults?.model;
  if (typeof defaults === "string" && defaults.trim()) {
    return `${defaults.trim()} (default)`;
  }
  if (defaults && typeof defaults === "object") {
    const primary = (defaults as { primary?: string }).primary;
    if (primary?.trim()) {
      return `${primary.trim()} (default)`;
    }
  }
  return "—";
}

export function resolveAgentModelPrimary(
  config: Record<string, unknown> | null,
  agentId: string,
): string | null {
  if (!config) {
    return null;
  }
  const entry = getAgentsConfigList(config).find(
    (e) => normalizeAgentId(e.id ?? "") === normalizeAgentId(agentId),
  );
  const entryModel = entry?.model;
  if (typeof entryModel === "string" && entryModel.trim()) {
    return entryModel.trim();
  }
  if (entryModel && typeof entryModel === "object") {
    const primary = (entryModel as { primary?: string }).primary;
    return primary?.trim() || null;
  }
  return null;
}

export function resolveAgentToolsProfile(
  config: Record<string, unknown> | null,
  agentId: string,
): string {
  if (!config) {
    return "full";
  }
  const entry = getAgentsConfigList(config).find(
    (e) => normalizeAgentId(e.id ?? "") === normalizeAgentId(agentId),
  );
  const agentProfile = entry?.tools?.profile;
  if (typeof agentProfile === "string" && agentProfile.trim()) {
    return agentProfile.trim();
  }
  const globalProfile = (config.tools as { profile?: string } | undefined)?.profile;
  if (typeof globalProfile === "string" && globalProfile.trim()) {
    return globalProfile.trim();
  }
  return "full";
}

export function resolveAgentSkillsAllowlist(
  config: Record<string, unknown> | null,
  agentId: string,
): string[] | null {
  if (!config) {
    return null;
  }
  const entry = getAgentsConfigList(config).find(
    (e) => normalizeAgentId(e.id ?? "") === normalizeAgentId(agentId),
  );
  if (!Array.isArray(entry?.skills)) {
    return null;
  }
  return entry.skills.map((s) => String(s).trim()).filter(Boolean);
}

export function patchAgentModel(
  config: Record<string, unknown>,
  agentId: string,
  modelId: string | null,
): void {
  const { list, index } = ensureAgentEntry(config, agentId);
  const entry = list[index];
  if (!entry) {
    return;
  }
  if (!modelId?.trim()) {
    delete entry.model;
    return;
  }
  const existing = entry.model;
  if (existing && typeof existing === "object" && !Array.isArray(existing)) {
    const fallbacks = (existing as { fallbacks?: unknown }).fallbacks;
    entry.model = {
      primary: modelId.trim(),
      ...(Array.isArray(fallbacks) ? { fallbacks } : {}),
    };
  } else {
    entry.model = modelId.trim();
  }
}

export function patchAgentToolsProfile(
  config: Record<string, unknown>,
  agentId: string,
  profile: string | null,
): void {
  const { list, index } = ensureAgentEntry(config, agentId);
  const entry = list[index];
  if (!entry) {
    return;
  }
  if (!profile?.trim()) {
    if (entry.tools) {
      delete entry.tools.profile;
      if (Object.keys(entry.tools).length === 0) {
        delete entry.tools;
      }
    }
    return;
  }
  entry.tools = { ...entry.tools, profile: profile.trim() };
}

export function patchAgentSkills(
  config: Record<string, unknown>,
  agentId: string,
  skillNames: string[] | null,
): void {
  const { list, index } = ensureAgentEntry(config, agentId);
  const entry = list[index];
  if (!entry) {
    return;
  }
  if (skillNames === null) {
    delete entry.skills;
    return;
  }
  entry.skills = [...new Set(skillNames.map((s) => s.trim()).filter(Boolean))];
}

export function patchAgentModelFallbacks(
  config: Record<string, unknown>,
  agentId: string,
  fallbacks: string[],
): boolean {
  if (findAgentListIndex(config, agentId) < 0) {
    return false;
  }
  const { list, index } = ensureAgentEntry(config, agentId);
  const entry = list[index];
  if (!entry) {
    return false;
  }
  const normalized = fallbacks.map((name) => name.trim()).filter(Boolean);
  const existing = entry.model;
  const resolvePrimary = (): string | null => {
    if (typeof existing === "string") {
      return existing.trim() || null;
    }
    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
      const primary = (existing as { primary?: unknown }).primary;
      if (typeof primary === "string") {
        const trimmed = primary.trim();
        return trimmed || null;
      }
    }
    return null;
  };
  const primary = resolvePrimary();
  if (normalized.length === 0) {
    if (primary) {
      entry.model = primary;
    } else {
      delete entry.model;
    }
    return true;
  }
  entry.model = primary ? { primary, fallbacks: normalized } : { fallbacks: normalized };
  return true;
}

export function patchAgentToolsClearAllow(
  config: Record<string, unknown>,
  agentId: string,
): boolean {
  if (findAgentListIndex(config, agentId) < 0) {
    return false;
  }
  const { list, index } = ensureAgentEntry(config, agentId);
  const entry = list[index];
  if (!entry?.tools?.allow) {
    return true;
  }
  delete entry.tools.allow;
  if (Object.keys(entry.tools).length === 0) {
    delete entry.tools;
  }
  return true;
}

export function patchAgentToolsOverrides(
  config: Record<string, unknown>,
  agentId: string,
  alsoAllow: string[],
  deny: string[],
): boolean {
  if (findAgentListIndex(config, agentId) < 0) {
    return false;
  }
  const { list, index } = ensureAgentEntry(config, agentId);
  const entry = list[index];
  if (!entry) {
    return false;
  }
  const tools = { ...entry.tools };
  const normalizedAllow = alsoAllow.map((name) => name.trim()).filter(Boolean);
  const normalizedDeny = deny.map((name) => name.trim()).filter(Boolean);
  if (normalizedAllow.length > 0) {
    tools.alsoAllow = normalizedAllow;
  } else {
    delete tools.alsoAllow;
  }
  if (normalizedDeny.length > 0) {
    tools.deny = normalizedDeny;
  } else {
    delete tools.deny;
  }
  if (Object.keys(tools).length === 0) {
    delete entry.tools;
  } else {
    entry.tools = tools;
  }
  return true;
}

export function patchAgentSkillToggle(
  config: Record<string, unknown>,
  agentId: string,
  params: {
    skillName: string;
    enabled: boolean;
    existingAllowlist: string[] | undefined;
    allSkillNames: string[];
  },
): boolean {
  const normalizedSkill = params.skillName.trim();
  if (!normalizedSkill || findAgentListIndex(config, agentId) < 0) {
    return false;
  }
  const hasConfiguredAllowlist = Array.isArray(params.existingAllowlist);
  if (!hasConfiguredAllowlist && params.allSkillNames.length === 0) {
    return false;
  }
  const base = params.existingAllowlist ?? params.allSkillNames;
  const next = new Set(base);
  if (params.enabled) {
    next.add(normalizedSkill);
  } else {
    next.delete(normalizedSkill);
  }
  patchAgentSkills(config, agentId, [...next]);
  return true;
}

export function patchAgentIdentityFields(
  config: Record<string, unknown>,
  agentId: string,
  patch: {
    identityName?: string;
    identityEmoji?: string;
    identityTheme?: string;
  },
): void {
  const { list, index } = ensureAgentEntry(config, agentId);
  const entry = list[index];
  if (!entry) {
    return;
  }
  if (patch.identityName !== undefined) {
    const trimmed = patch.identityName.trim();
    if (trimmed) {
      entry.identity = { ...entry.identity, name: trimmed };
    } else if (entry.identity) {
      delete entry.identity.name;
    }
  }
  if (patch.identityEmoji !== undefined) {
    const trimmed = patch.identityEmoji.trim();
    if (trimmed) {
      entry.identity = { ...entry.identity, emoji: trimmed };
    } else if (entry.identity) {
      delete entry.identity.emoji;
    }
  }
  if (patch.identityTheme !== undefined) {
    const trimmed = patch.identityTheme.trim();
    if (trimmed) {
      entry.identity = { ...entry.identity, theme: trimmed };
    } else if (entry.identity) {
      delete entry.identity.theme;
    }
  }
}
